import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApplicationStore } from '../../store/application.store';
import type { LegalContext } from '../../types/legal-context.types';
import { getApplicationBrandTheme } from '../../utils/brand-theme.utils';

interface TermsConsentFieldProps {
  onChange: (checked: boolean) => void;
  value: boolean;
  disabled?: boolean;
  isInvalid?: boolean;
  legalContext?: LegalContext | null;
}

const defaultLink = (path: string) => path;

/**
 * Renders the required final clickwrap consent field.
 */
export const TermsConsentField = ({
  onChange,
  value,
  disabled = false,
  isInvalid = false,
  legalContext = null,
}: TermsConsentFieldProps) => {
  const { structure, userShortId } = useApplicationStore();
  const brandTheme = getApplicationBrandTheme(structure?.theme, userShortId);

  const brokerTermsLink =
    legalContext?.clickwrap?.links?.brokerTerms ||
    defaultLink(`/legal/terms?shortId=${userShortId || ''}`);
  const brokerPrivacyLink =
    legalContext?.clickwrap?.links?.brokerPrivacy ||
    defaultLink(`/legal/privacy?shortId=${userShortId || ''}`);
  const brokerDisclaimerLink =
    legalContext?.clickwrap?.links?.brokerDisclaimer ||
    defaultLink(`/legal/disclaimer?shortId=${userShortId || ''}`);
  const platformTermsLink =
    legalContext?.clickwrap?.links?.platformTerms || defaultLink('/legal/terms');
  const platformPrivacyLink =
    legalContext?.clickwrap?.links?.platformPrivacy || defaultLink('/legal/privacy');

  const finalClickwrapText =
    legalContext?.clickwrap?.finalClickwrapText?.trim() ||
    'By clicking Submit, you acknowledge and agree to the legal documents linked below.';

  const renderValidationError = () => {
    if (!isInvalid) return null;

    return (
      <div className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        You must agree to the terms to continue
      </div>
    );
  };

  return (
    <div className="mb-4">
      <label
        className={`flex items-start space-x-2 text-sm font-medium ${disabled ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700'}`}
      >
        <input
          type="checkbox"
          className={`mt-1 h-4 w-4 rounded ${
            disabled ? 'border-gray-200 bg-gray-100 cursor-not-allowed' : 'border-gray-300'
          } ${isInvalid ? 'border-red-500 ring-1 ring-red-500' : ''}`}
          style={{
            accentColor: disabled ? undefined : brandTheme.actionBackground,
          }}
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          required={true}
          disabled={disabled}
          data-testid="terms-consent-checkbox"
        />
        <span className="flex-1 text-gray-700">
          {finalClickwrapText}{' '}
          <Link
            to={brokerTermsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: brandTheme.linkColor }}
          >
            Broker Terms
          </Link>
          {', '}
          <Link
            to={brokerPrivacyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: brandTheme.linkColor }}
          >
            Broker Privacy Policy
          </Link>
          {', '}
          <Link
            to={brokerDisclaimerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: brandTheme.linkColor }}
          >
            Broker Disclaimer
          </Link>
          {', '}
          <Link
            to={platformTermsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: brandTheme.linkColor }}
          >
            Platform Terms
          </Link>
          {' and '}
          <Link
            to={platformPrivacyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: brandTheme.linkColor }}
          >
            Platform Privacy Policy
          </Link>
          .<span className="text-red-500">*</span>
        </span>
      </label>
      {renderValidationError()}
    </div>
  );
};
